import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { PACKAGE_CHANGE_DOWNGRADE_TYPES, RESERVATION_STATE_PENDING } from 'constants/reservation'
import useOffer from 'hooks/Offers/useOffer'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import { useAppSelector } from 'hooks/reduxHooks'
import { ITEM_STATUS_AWAITING_DATES, ORDER_STATUS_COMPLETED } from 'constants/cart'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import { HotelSummaryInclusionsV2 } from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/HotelSummaryInclusionsV2'
import Action, { ActionsList } from '../../Chat/MessageMeta/Action'
import LesaPurchaseSummaryMetaContainer from './Common/LesaPurchaseSummaryMetaContainer'
import { filterActions } from '../../../utils/actionsUtils'
import LesaHotelItemSummary from 'supportAssistant/components/SelectPurchase/LesaPurchaseSummary/LesaHotelItemSummary'
import FinePrintModal from '../../Chat/MessageMeta/FinePrintModal'
import { ChatActions, ContentTypes, MessageMetaContent, MessageMetaPayload } from 'api/support'

const LesaHotelItemSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

interface Props {
  order: App.Order;
  isPastOrder: boolean;
  hideSelect?: boolean;
  actions?: MessageMetaContent[];
  componentsMap: Map<string | undefined, MessageMetaContent>;
  payload?: MessageMetaPayload
  handleAction: (message: string, meta: any) => void;
}

export default function LesaHotelPurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect } = props
  const { actions, handleAction, payload, componentsMap } = props

  const [offer] = useOffer<App.Offer>(order.items[0]?.offerId)
  let items = order.items?.filter(i => !(i.status === 'cancelled' && PACKAGE_CHANGE_DOWNGRADE_TYPES.includes(i.downgradeType)))

  // In case all items are cancelled, show the latest one
  if (items.length === 0) {
    items = order.items.slice(-1)
  }

  const item = items[0]

  const orderOffer = item?.offer
  const isTour = orderOffer?.parentType === OFFER_TYPE_TOUR

  const hasExperiences = !!order.experienceItems.length
  const hasAddons = !!order.addonItems.length
  const hasInsurance = !!order.insuranceItems.length
  const hasFlight = !!order.hasFlight

  const insuranceProductName = useAppSelector(state => state.geo.insuranceProductName)

  const hasInclusionsV2 = item?.inclusions?.inclusionsV2 && item?.inclusions?.inclusionsV2.length > 0

  if (!offer) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, isTour ? [ChatActions.LIST_INCLUSIONS, ChatActions.SHOW_FINE_PRINT] : []))

  return (
    <>
      {(!!componentsMap?.get(ContentTypes.LIST_ORDERS) || !!componentsMap?.get(ChatActions.SELECT_ORDER)) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS" />}>
              {isTour ? 'Tour' : 'Hotel'}
              {hasFlight && ' + Flights'}
              {(hasExperiences || hasAddons) && ' + Experiences'}
              {hasInsurance && ` + Travel ${insuranceProductName} - ${order.insuranceItems[0]?.productName}`}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical">
            <Group direction="vertical" gap={4}>
              {orderOffer?.location && <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{orderOffer.location}</CaptionTextBlock>}
              <BodyTextBlock lineClamp={2} weight="bold" variant="medium">{orderOffer?.name}</BodyTextBlock>{' '}
              {items.map(item => {
                let status: string = item.status

                if (isTour && item.reservation) {
                  if (item.onHold) {
                    status = ITEM_STATUS_AWAITING_DATES
                  } else if (order.status === ORDER_STATUS_COMPLETED && item.reservation.state === RESERVATION_STATE_PENDING) {
                    status = RESERVATION_STATE_PENDING
                  } else if (item.reservation.travellerDetailsRequired) {
                    status = 'needsDetails'
                  }
                }

                return <LesaPurchaseSummaryItineraryDates
                  key={item.id}
                  status={status}
                  showReservationDates={!item.onHold && item.reservation && item.reservation.state !== RESERVATION_STATE_PENDING}
                  startDate={item.reservation?.startDate}
                  endDate={item.reservation?.endDate}
                  duration={item.duration ? (item.duration && item.durationLabel) : undefined}
                  bookingNumber={item.bookingNumber}
                />
              })}
            </Group>
          </Group>
          {!hideSelect && <Group fullWidth direction="horizontal" horizontalAlign="end" verticalAlign="end">
            <TextButton onClick={() => handleAction?.('', { key: ChatActions.SELECT_ORDER, payload: { id_orders: order.id, orderType: 'hotel' } })} fit="mobile-full-width" size="small" kind="secondary">
              Select
            </TextButton>
          </Group>}
        </LesaPurchaseSummaryContainer>
      )}
      {!!componentsMap?.get(ContentTypes.FINE_PRINT_MODAL) && (
        <FinePrintModal offer={offer} />
      )}
      {!!componentsMap?.get(ChatActions.LIST_INCLUSIONS) && (
        <LesaPurchaseSummaryMetaContainer>
          {item?.inclusions?.inclusionHighlights &&
            <>
              <BodyTextBlock variant="medium" weight="bold">Package inclusions:</BodyTextBlock>
              <MarkdownRender fontSize="medium" type="compact" content={item.inclusions.inclusionHighlights} />
            </>
          }
          {item?.inclusions?.bonusInclusions &&
            <>
              <BodyTextBlock variant="medium" weight="bold">Bonus inclusions:</BodyTextBlock>
              <MarkdownRender fontSize="medium" type="compact" content={item.inclusions.bonusInclusions} />
            </>
          }
          {(!item?.inclusions?.inclusionHighlights && !item?.inclusions?.bonusInclusions && hasInclusionsV2) &&
            <HotelSummaryInclusionsV2 inclusions={item.inclusions.inclusionsV2 as App.PackageInclusionV2[]} />
          }
        </LesaPurchaseSummaryMetaContainer>
      )}
      {!!componentsMap?.get(ContentTypes.LIST_ORDER_ITEMS) && (
        <LesaHotelItemSummaryContainer>
          {items?.map((hotelItem, index) => (
            <LesaHotelItemSummary
              key={index}
              item={hotelItem}
              order={order}
              offer={offer}
              handleItemSelect={handleAction}
              roomLabel={items?.length === 1 ? 'Your Room' : `Room ${index + 1}`}
            />
          ))}
        </LesaHotelItemSummaryContainer>
      )}
      {!!componentsMap?.get(ChatActions.SELECT_ORDER_ITEM) && (
        <LesaHotelItemSummaryContainer>
          <LesaHotelItemSummary
              hideSelectButton
              item={items.find(hotelItem => hotelItem.id === payload?.itemId)}
              order={order}
              offer={offer}
              roomLabel="Selected room"
          />
        </LesaHotelItemSummaryContainer>
      )}
      {!!orderActions?.length && <ActionsList>
        {orderActions?.map((action) => (
          <Action
            key={action.action?.key}
            action={action}
            payload={payload}
            handleAction={(text, meta) => handleAction(text, { ...meta, orderType: 'hotel' })}
          />
        ))}
      </ActionsList>}
    </>
  )
}
